import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const APP_NAME = 'Abrasoft Hub';

const DEFAULT_DESCRIPTION =
  'Abrasoft Hub is a single portal for Abrasoft services, helping businesses manage operations and reporting in one place.';

const DEFAULT_OG_IMAGE = `${import.meta.env.BASE_URL}abrasoft-hub-og.webp`;

function getHubSectionDescription(pathname: string): string {
  // Keep this descriptive but non-generic; crawlers/social cards benefit from unique text.
  if (pathname === '/') {
    return 'Sign in to Abrasoft Hub to access Abrasoft services in one place: master data, transactions, POS, memberships, and reporting.';
  }

  if (pathname.startsWith('/dashboard')) {
    return 'Your command center in Abrasoft Hub: quickly review key service activity and keep operations moving.';
  }

  if (pathname.startsWith('/sales')) {
    return 'Track sales in Abrasoft Hub—from quick POS workflows to detailed sales history and review.';
  }

  if (pathname.startsWith('/expenses')) {
    return 'Handle disbursements and expense records in Abrasoft Hub with structured descriptions and category-driven reporting.';
  }

  if (pathname.startsWith('/products') || pathname.startsWith('/inventory')) {
    return 'Manage products and inventory in Abrasoft Hub, including inventory views tied to your service operations.';
  }

  if (pathname.startsWith('/customers') || pathname.startsWith('/suppliers')) {
    return 'Maintain your business contacts in Abrasoft Hub—customers and suppliers—so services stay accurate across transactions.';
  }

  if (pathname.startsWith('/reports')) {
    return 'Generate business reports in Abrasoft Hub, including income statements, balance sheets, cash flow, and general ledgers.';
  }

  if (pathname.startsWith('/pos')) {
    return 'Run Point of Sale (POS) in Abrasoft Hub with payment flows designed for speed—customers, products, and confirmations included.';
  }

  if (pathname.startsWith('/membership')) {
    return 'Manage memberships in Abrasoft Hub: invitations, history, settings, and content for your community or organization.';
  }

  if (
    pathname.startsWith('/user/complete-profile') ||
    pathname.startsWith('/user/profile') ||
    pathname.startsWith('/user/change-password')
  ) {
    return 'Update your profile in Abrasoft Hub to keep account details and company access accurate.';
  }

  if (pathname.startsWith('/company')) {
    return 'Create and manage companies in Abrasoft Hub to organize services, access, and collaboration for your team.';
  }

  if (pathname.startsWith('/storage')) {
    return 'Use storage in Abrasoft Hub to manage files and service assets securely for your workflows.';
  }

  if (pathname.startsWith('/schedules')) {
    return 'Configure company-scoped cron schedules in Abrasoft Hub; the scheduler service invokes your webhooks on each run.';
  }

  if (pathname.startsWith('/kanban')) {
    return 'Coordinate work with Kanban in Abrasoft Hub for a clear operational view.';
  }

  if (pathname.startsWith('/chat')) {
    return 'Get assistance in Abrasoft Hub with in-app chat support for faster workflows.';
  }

  return DEFAULT_DESCRIPTION;
}

// Map routes to page titles
const routeTitles: Record<string, string> = {
  '/': 'Sign In',
  '/dashboard': 'Dashboard',
  '/membership': "General Member's List",
  '/membership/add': 'Create Membership',
  '/membership/history': 'Membership History',
  '/membership/pending': 'Pending Membership Invitations',
  '/membership/rollback': 'Rollback Membership',
  '/membership/statistics': 'Membership Statistics',
  '/sales': 'Sales',
  '/sales/add': 'New Sale',
  '/expenses': 'Expenses',
  '/reports': 'Reports',
  '/reports/income-statement': 'Statement of Comprehensive Income',
  '/reports/balance-sheet': 'Statement of Financial Position',
  '/reports/cash-flow-statement': 'Statement of Cash Flow',
  '/reports/statement-of-changes-in-equity': 'Statement of Changes in Equity',
  '/reports/general-ledger': 'General Ledger',
  '/reports/trial-balance': 'Trial Balance',
  '/reports/general-journal': 'General Journal',
  '/reports/inventory': 'Inventory Report',
  '/products': 'Products',
  '/products/add': 'Add Product',
  '/inventory': 'Inventory',
  '/inventory/add': 'Add Inventory',
  '/schedules': 'Schedules',
  '/customers': 'Customers',
  '/suppliers': 'Suppliers',
  '/companies': 'Companies',
  '/company/lobby': 'Select Company',
  '/pos': 'Point of Sale',
  '/pos/payment': 'Payment',
  '/pos/success': 'Payment Success',
  '/pos/settings': 'Printer Settings',
  '/user/profile': 'Profile',
  '/user/change-password': 'Change Password',
  '/user/complete-profile': 'Complete Profile',
};

// Function to get title from path (handles dynamic routes like /sales/:id)
function getTitleFromPath(pathname: string): string {
  // Check exact match first
  if (routeTitles[pathname]) {
    return routeTitles[pathname];
  }

  // Handle dynamic routes
  if (pathname.startsWith('/sales/') && pathname !== '/sales/add') {
    return 'View Sale';
  }
  if (pathname.startsWith('/expenses/')) {
    return 'View Expense';
  }
  if (pathname.startsWith('/products/') && !pathname.includes('/add')) {
    if (pathname.includes('/edit')) {
      return 'Edit Product';
    }
    if (pathname.includes('/inventory')) {
      return 'Product Inventory';
    }
  }
  if (pathname.startsWith('/inventory/') && pathname !== '/inventory/add') {
    return 'Product Inventory';
  }
  if (pathname.startsWith('/membership/') && pathname.endsWith('/edit')) {
    return 'Update membership';
  }

  // Default: use the last segment of the path
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length > 0) {
    const lastSegment = segments[segments.length - 1];
    // Capitalize first letter
    return (
      lastSegment.charAt(0).toUpperCase() +
      lastSegment.slice(1).replace(/-/g, ' ')
    );
  }

  return APP_NAME;
}

function PageTitle() {
  const location = useLocation();

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const title = getTitleFromPath(location.pathname);
    const fullTitle = title ? `${title} | ${APP_NAME}` : APP_NAME;
    const description = getHubSectionDescription(location.pathname);
    const href = window.location.href;
    let ogImageUrl = DEFAULT_OG_IMAGE;
    try {
      // Resolve to an absolute URL for social previews.
      ogImageUrl = new URL(DEFAULT_OG_IMAGE, window.location.origin).href;
    } catch {
      // Keep DEFAULT_OG_IMAGE as-is if URL resolution fails.
    }

    document.title = fullTitle;

    const upsertMeta = ({
      name,
      property,
      content,
    }: {
      name?: string;
      property?: string;
      content: string;
    }) => {
      const selector = property
        ? `meta[property="${property}"]`
        : `meta[name="${name}"]`;
      const existing = document.head.querySelector(selector);

      if (existing instanceof HTMLMetaElement) {
        existing.content = content;
        return;
      }

      const meta = document.createElement('meta');
      if (property) meta.setAttribute('property', property);
      if (name) meta.setAttribute('name', name);
      meta.setAttribute('content', content);
      document.head.appendChild(meta);
    };

    upsertMeta({ name: 'description', content: description });

    upsertMeta({ property: 'og:title', content: fullTitle });
    upsertMeta({ property: 'og:description', content: description });
    upsertMeta({ property: 'og:site_name', content: APP_NAME });
    upsertMeta({
      property: 'og:image',
      content: ogImageUrl,
    });
    upsertMeta({ property: 'og:url', content: href });

    upsertMeta({ name: 'twitter:title', content: fullTitle });
    upsertMeta({ name: 'twitter:description', content: description });
    upsertMeta({ name: 'twitter:image', content: ogImageUrl });

    const canonicalLink = document.getElementById(
      'canonical',
    ) as HTMLLinkElement | null;
    if (canonicalLink) canonicalLink.href = href;

    const ldJsonScript = document.getElementById(
      'abrasoft-hub-ld-json',
    ) as HTMLScriptElement | null;
    if (ldJsonScript) {
      const ld = {
        '@context': 'https://schema.org',
        '@type': 'WebApplication',
        name: APP_NAME,
        applicationCategory: 'BusinessApplication',
        description,
        operatingSystem: 'Web',
        url: href,
      };
      ldJsonScript.textContent = JSON.stringify(ld, null, 2);
    }
  }, [location.pathname]);

  return null; // This component doesn't render anything
}

export default PageTitle;
